import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { initTheme } from "@/lib/theme";
import { setSeo } from "@/lib/seo";
import api from "@/lib/api";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { MarketingNav } from "@/components/MarketingNav";
import { MarketingFooter } from "@/components/MarketingFooter";
import { Eyebrow } from "@/components/Eyebrow";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight, Play, ScanLine, Sparkles, ImageIcon, CalendarClock,
  Check, Star, Quote,
} from "lucide-react";

const CLIENTS = ["FedEx", "PNC", "Pfizer", "Merck", "Rosetta Stone", "Carnegie Mellon", "U.S. Dept. of Education", "National PTA"];

const STEPS = [
  {
    icon: ScanLine,
    n: "01",
    title: "Analyze your site",
    body: "Paste any URL. We scan your live site — colors, typography, imagery and tone — and distill it into an editable Brand DNA profile in about a minute.",
    visual: "dna",
  },
  {
    icon: Sparkles,
    n: "02",
    title: "Generate on-brand ideas",
    body: "AI drafts campaign concepts, captions and hashtags that actually sound like you — grounded in your brand voice, not generic filler.",
    visual: "idea",
  },
  {
    icon: ImageIcon,
    n: "03",
    title: "Render branded visuals",
    body: "Turn any idea into polished, on-palette imagery and carousels. Multiple slides render in parallel, right in your brand colors.",
    visual: "image",
  },
  {
    icon: CalendarClock,
    n: "04",
    title: "Schedule & publish",
    body: "Connect your accounts through Publer and schedule finished posts to a calendar — or export assets and captions to post yourself.",
    visual: "schedule",
  },
];

const TIERS = [
  {
    name: "Starter",
    price: "$249",
    sub: "14-day free trial · 1 brand",
    features: ["1 brand workspace + Brand DNA", "Up to 40 generated posts / month", "Competitor & industry research", "Swipe-to-approve + revisions", "Calendar scheduling"],
    cta: "Start free trial",
    action: "start",
    recommended: false,
  },
  {
    name: "Pro",
    price: "$499",
    sub: "14-day free trial · up to 10 brands",
    features: ["Everything in Starter", "Up to 120 generated posts / month", "Autopilot batch generation", "Content pillars & angle library", "Export assets & captions"],
    cta: "Start free trial",
    action: "start",
    recommended: true,
  },
  {
    name: "Enterprise",
    price: "$1,499",
    sub: "For studios at scale",
    features: ["Everything in Pro", "Up to 500 generated posts / month", "Unlimited brand workspaces", "Team seats & approval workflow", "Dedicated HeyDesignGuy strategist"],
    cta: "Get started",
    action: "start",
    recommended: false,
  },
];

const TESTIMONIALS = [
  { quote: "I pasted our Shopify URL and had a week of on-brand posts before lunch. It nailed our voice better than the freelancer we'd been paying.", name: "Maya R.", role: "Founder, Wildgrain Studio" },
  { quote: "As an agency we onboard a new brand kit in minutes instead of days. The brand DNA extraction is genuinely uncanny.", name: "Devin K.", role: "Creative Director, North & Co." },
  { quote: "Finally, agency-level brand thinking I can actually afford. The scheduling pipeline means I post consistently for the first time ever.", name: "Tariq S.", role: "Solo founder, Lumen Coffee" },
];

const FAQS = [
  { q: "Do I need a credit card to start?", a: "Every plan starts with a 14-day free trial so you can build your first month of content before you commit. Cancel anytime during the trial." },
  { q: "What does the analysis actually read?", a: "We scan your live site for colors, typography, imagery and brand voice, then distill it into an editable Brand DNA profile you fully control." },
  { q: "Can I edit what it finds?", a: "Yes. Every color, font, and voice note is editable inside the studio — refine anything before you generate." },
  { q: "How does publishing work?", a: "Connect your social accounts through Publer to schedule finished posts to a calendar, or export the assets and captions to post manually." },
  { q: "Is my brand data private?", a: "Your brand profiles are scoped to your account. We never share or sell your data." },
  { q: "Can I upgrade later?", a: "Absolutely. Start on Starter and move to Pro or Enterprise whenever you're ready." },
];


function ExampleVisual({ kind, data }) {
  if (kind === "dna") {
    const b = data?.brand;
    const brandName = b?.brandName || "Wildgrain Studio";
    const colors = (b?.colors?.length ? b.colors : ["#F97316", "#1F2937", "#FBBF24", "#10B981", "#EF4444"]).slice(0, 5);
    const fonts = (b?.fonts?.length ? b.fonts : ["Plus Jakarta Sans", "Inter"]).slice(0, 2);
    const voice = b?.voiceSummary || "Warm, grounded and a little rebellious — speaks to makers who care about craft.";
    return (
      <div className="panel-card" style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", letterSpacing: "0.08em" }}>BRAND DNA</div>
          {b && <span className="font-mono" style={{ fontSize: 9, color: "var(--orange)", letterSpacing: "0.08em" }}>● LIVE</span>}
        </div>
        <div className="font-display" style={{ fontSize: 18, fontWeight: 700, color: "var(--tprimary)", marginTop: 6 }}>{brandName}</div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          {colors.map((c, i) => (
            <div key={`${c}-${i}`} style={{ width: 38, height: 38, borderRadius: 8, background: c, border: "1px solid rgba(255,255,255,0.08)" }} />
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          {fonts.map((f, i) => (
            <span key={`${f}-${i}`} className="font-mono" style={{ fontSize: 11, padding: "4px 10px", borderRadius: 999, border: "1px solid var(--cardhover)", color: "var(--tsecondary)" }}>{f}</span>
          ))}
        </div>
        <p className="font-body" style={{ fontSize: 13, color: "var(--tsecondary)", marginTop: 14, lineHeight: 1.6 }}>
          {voice}
        </p>
      </div>
    );
  }
  if (kind === "idea") {
    const i = data?.idea;
    const title = i?.title || "The 6am Loaf Ritual";
    const caption = i?.caption || "Some mornings deserve more than coffee. Fresh-milled, slow-risen, worth the wait.";
    const hashtags = (i?.hashtags?.length ? i.hashtags : ["slowfood", "craftbaking", "wildgrain"]).slice(0, 3);
    return (
      <div className="panel-card" style={{ padding: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}><span>{i ? "From a live brand kit" : "Campaign idea"}</span></div>
        <div className="font-display" style={{ fontSize: 17, fontWeight: 700, color: "var(--tprimary)" }}>{title}</div>
        <p className="font-body" style={{ fontSize: 13.5, color: "var(--tsecondary)", marginTop: 10, lineHeight: 1.6 }}>
          "{caption}"
        </p>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {hashtags.map((h, idx) => (
            <span key={`${h}-${idx}`} className="font-mono" style={{ fontSize: 11, color: "var(--orange)" }}>#{String(h).replace(/^#/, "")}</span>
          ))}
        </div>
      </div>
    );
  }
  if (kind === "image") {
    return (
      <div className="panel-card" style={{ padding: 14 }}>
        <div style={{ borderRadius: 10, overflow: "hidden", aspectRatio: "1 / 1" }}>
          <img src="/marketing/render-sample.png" alt="Example AI-rendered on-brand social post" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ flex: 1, height: 5, borderRadius: 999, background: i === 0 ? "var(--orange)" : "var(--cardhover)" }} />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="panel-card" style={{ padding: 20 }}>
      <div className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", letterSpacing: "0.08em", marginBottom: 12 }}>SCHEDULED</div>
      {[
        { d: "Mon", t: "9:00 AM", p: "Instagram" },
        { d: "Wed", t: "12:30 PM", p: "LinkedIn" },
        { d: "Fri", t: "5:00 PM", p: "Facebook" },
      ].map((s) => (
        <div key={s.d} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid var(--line)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--ok)" }} />
            <span className="font-display" style={{ fontSize: 14, fontWeight: 600, color: "var(--tprimary)" }}>{s.d} · {s.t}</span>
          </div>
          <span className="font-mono" style={{ fontSize: 11, color: "var(--tsecondary)" }}>{s.p}</span>
        </div>
      ))}
    </div>
  );
}

export default function MarketingLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [url, setUrl] = useState("");
  const [showcase, setShowcase] = useState(null);
  useScrollReveal();

  useEffect(() => { initTheme(); }, []);

  useEffect(() => {
    setSeo({
      title: "HDG Creative Studio — Paste a URL, get on-brand content in minutes",
      description: "AI-powered brand studio by HeyDesignGuy. Paste your website URL to extract your brand colors, fonts and voice, then generate on-brand ideas, visuals and scheduled social posts — backed by 25+ years of brand strategy.",
      canonical: "https://creativestudio.demosforclients.com/",
    });
    api.get("/public/showcase").then(({ data }) => setShowcase(data || null)).catch(() => {});
  }, []);

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (target) {
      const t = setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      return () => clearTimeout(t);
    }
  }, [location.state]);

  const startWithUrl = () => navigate("/start", { state: { url: url.trim() } });

  return (
    <div className="lightboard" data-testid="marketing-landing" style={{ minHeight: "100vh" }}>
      <MarketingNav />

      {/* ---------- HERO ---------- */}
      <section style={{ maxWidth: 1140, margin: "0 auto", padding: "72px 22px 64px" }}>
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
          <div className="mkt-fade-up in-view">
            <Eyebrow testId="hero-eyebrow">14-day free trial · 25+ years of brand strategy</Eyebrow>
            <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 64px)", fontWeight: 800, letterSpacing: "-0.035em", lineHeight: 1.02, color: "var(--tprimary)", marginTop: 22 }}>
              Paste a URL.<br />Watch your brand<br /><span style={{ color: "var(--orange)" }}>become a content engine.</span>
            </h1>
            <p className="font-body" style={{ fontSize: 18, color: "var(--tsecondary)", marginTop: 22, lineHeight: 1.55, maxWidth: 520 }}>
              HDG Creative Studio reads your website, learns your brand, and generates on-brand ideas, visuals and scheduled posts — in about a minute.
            </p>

            <div style={{ display: "flex", gap: 10, marginTop: 30, maxWidth: 540, flexWrap: "wrap" }}>
              <input
                data-testid="hero-url-input"
                className="dim-input font-body"
                style={{ flex: 1, minWidth: 220, height: 54, fontSize: 15 }}
                placeholder="yourbrand.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && startWithUrl()}
                aria-label="Your website URL"
              />
              <button data-testid="hero-start-button" className="btn-primary" style={{ height: 54, padding: "0 24px", fontSize: 15 }} onClick={startWithUrl}>
                Analyze my brand <ArrowRight size={16} />
              </button>
            </div>
            <p className="font-mono" style={{ fontSize: 11.5, color: "var(--tmuted)", marginTop: 14, letterSpacing: "0.03em" }}>
              See your colors, voice & first posts before you sign up.
            </p>
          </div>

          <div className="mkt-fade-up in-view" style={{ animationDelay: "0.12s" }}>
            <div className="panel-card grid-pulse" style={{ padding: 12, overflow: "hidden" }}>
              <img src="/marketing/hero.png" alt="Abstract visualization of brand DNA extraction" style={{ width: "100%", borderRadius: 10, display: "block" }} />
            </div>
          </div>
        </div>

        {/* client strip */}
        <div className="mkt-fade-up" style={{ marginTop: 64 }}>
          <p className="font-mono" style={{ fontSize: 11, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.1em", textAlign: "center" }}>
            Built on brand work for
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "18px 38px", justifyContent: "center", marginTop: 18 }}>
            {CLIENTS.map((c) => (
              <span key={c} className="font-display" style={{ fontSize: 16, fontWeight: 700, color: "var(--tsecondary)", opacity: 0.7 }}>{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- DEMO ---------- */}
      <section id="demo" style={{ maxWidth: 1000, margin: "0 auto", padding: "20px 22px 72px" }}>
        <div className="mkt-fade-up" style={{ textAlign: "center", marginBottom: 28 }}>
          <Eyebrow className="!justify-center" testId="demo-eyebrow">See it in action</Eyebrow>
          <h2 className="font-display" style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 800, letterSpacing: "-0.025em", color: "var(--tprimary)", marginTop: 14 }}>
            From homepage to posting queue
          </h2>
        </div>
        <div className="mkt-fade-up panel-card" data-testid="demo-video" style={{ padding: 12, position: "relative", overflow: "hidden" }}>
          <img src="/marketing/demo.png" alt="HDG Creative Studio interface preview" style={{ width: "100%", borderRadius: 10, display: "block" }} />
          <button
            data-testid="demo-play-button"
            aria-label="Play demo"
            style={{
              position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
              width: 78, height: 78, borderRadius: 999, background: "var(--orange)", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 0 0 10px rgba(249,115,22,0.18), 0 0 44px rgba(249,115,22,0.4)",
            }}
          >
            <Play size={30} fill="#0D0D0D" color="#0D0D0D" style={{ marginLeft: 4 }} />
          </button>
          <span className="font-mono" style={{ position: "absolute", bottom: 22, left: 24, fontSize: 11, color: "var(--tprimary)", background: "rgba(13,13,13,0.6)", padding: "4px 10px", borderRadius: 999 }}>
            Product walkthrough · coming soon
          </span>
        </div>
      </section>

      {/* ---------- HOW IT WORKS ---------- */}
      <section id="how" style={{ maxWidth: 1080, margin: "0 auto", padding: "8px 22px 60px" }}>
        <div className="mkt-fade-up" style={{ textAlign: "center", marginBottom: 52 }}>
          <Eyebrow className="!justify-center" testId="how-eyebrow">How it works</Eyebrow>
          <h2 className="font-display" style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 800, letterSpacing: "-0.025em", color: "var(--tprimary)", marginTop: 14 }}>
            Four steps. One minute. Zero busywork.
          </h2>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 64 }}>
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            const reversed = i % 2 === 1;
            return (
              <div
                key={step.n}
                data-testid={`how-step-${i + 1}`}
                className="mkt-fade-up grid grid-cols-1 lg:grid-cols-2 gap-9 lg:gap-12 items-center"
              >
                <div className={reversed ? "lg:order-2" : ""}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ width: 46, height: 46, borderRadius: 12, background: "rgba(249,115,22,0.1)", border: "1px solid var(--cardhover)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon size={22} style={{ color: "var(--orange)" }} />
                    </div>
                    <span className="font-mono" style={{ fontSize: 28, fontWeight: 700, color: "var(--tmuted)", opacity: 0.5 }}>{step.n}</span>
                  </div>
                  <h3 className="font-display" style={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--tprimary)", marginTop: 20 }}>{step.title}</h3>
                  <p className="font-body" style={{ fontSize: 16, color: "var(--tsecondary)", marginTop: 12, lineHeight: 1.65, maxWidth: 440 }}>{step.body}</p>
                </div>
                <div className={reversed ? "lg:order-1" : ""}>
                  <ExampleVisual kind={step.visual} data={showcase} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------- PRICING ---------- */}
      <section id="pricing" style={{ maxWidth: 1100, margin: "0 auto", padding: "56px 22px 64px" }}>
        <div className="mkt-fade-up" style={{ textAlign: "center", marginBottom: 44 }}>
          <Eyebrow className="!justify-center" testId="pricing-eyebrow">Pricing</Eyebrow>
          <h2 className="font-display" style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 800, letterSpacing: "-0.025em", color: "var(--tprimary)", marginTop: 14 }}>
            Start free. Scale when you're ready.
          </h2>
          <p className="font-body" style={{ fontSize: 16, color: "var(--tsecondary)", marginTop: 12 }}>
            Agency-caliber brand thinking — without the agency retainer.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-[22px]">
          {TIERS.map((t) => (
            <div
              key={t.name}
              data-testid={`pricing-${t.name.toLowerCase()}`}
              className={`mkt-fade-up panel-card ${t.recommended ? "recommended-glow" : ""}`}
              style={{ padding: 28, position: "relative", display: "flex", flexDirection: "column" }}
            >
              {t.recommended && (
                <span className="font-mono" style={{ position: "absolute", top: -11, left: 28, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", background: "var(--orange)", color: "#0D0D0D", padding: "4px 10px", borderRadius: 999, textTransform: "uppercase" }}>
                  Most popular
                </span>
              )}
              <div className="font-display" style={{ fontSize: 18, fontWeight: 700, color: "var(--tprimary)" }}>{t.name}</div>
              <div className="font-display" style={{ fontSize: 36, fontWeight: 800, letterSpacing: "-0.02em", color: "var(--tprimary)", marginTop: 10 }}>{t.price}</div>
              <div className="font-body" style={{ fontSize: 13, color: "var(--tmuted)", marginTop: 4 }}>{t.sub}</div>

              <div style={{ display: "flex", flexDirection: "column", gap: 11, marginTop: 22, flex: 1 }}>
                {t.features.map((f) => (
                  <div key={f} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <Check size={16} style={{ color: "var(--orange)", marginTop: 2, flexShrink: 0 }} />
                    <span className="font-body" style={{ fontSize: 13.5, color: "var(--tsecondary)" }}>{f}</span>
                  </div>
                ))}
              </div>

              {t.action === "start" ? (
                <button data-testid={`pricing-cta-${t.name.toLowerCase()}`} onClick={() => navigate("/start")} className="btn-primary" style={{ width: "100%", marginTop: 26, padding: "12px" }}>
                  {t.cta} <ArrowRight size={15} />
                </button>
              ) : (
                <a data-testid={`pricing-cta-${t.name.toLowerCase()}`} href={`mailto:hello@heydesignguy.com?subject=${encodeURIComponent(`HDG Creative Studio — ${t.name} plan enquiry`)}`} className={t.recommended ? "btn-primary" : "btn-secondary"} style={{ width: "100%", marginTop: 26, padding: "12px", textDecoration: "none" }}>
                  {t.cta}
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ---------- TESTIMONIALS ---------- */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 22px 64px" }}>
        <div className="mkt-fade-up" style={{ textAlign: "center", marginBottom: 40 }}>
          <Eyebrow className="!justify-center" testId="testimonials-eyebrow">Loved by builders</Eyebrow>
          <h2 className="font-display" style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 800, letterSpacing: "-0.025em", color: "var(--tprimary)", marginTop: 14 }}>
            Brand work that used to take a team
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[22px]">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} data-testid={`testimonial-${i + 1}`} className="mkt-fade-up panel-card" style={{ padding: 26, display: "flex", flexDirection: "column" }}>
              <Quote size={22} style={{ color: "var(--orange)" }} />
              <div style={{ display: "flex", gap: 3, marginTop: 14 }}>
                {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={14} fill="var(--orange)" color="var(--orange)" />)}
              </div>
              <p className="font-body" style={{ fontSize: 15, color: "var(--tprimary)", marginTop: 14, lineHeight: 1.6, flex: 1 }}>"{t.quote}"</p>
              <div style={{ marginTop: 18 }}>
                <div className="font-display" style={{ fontSize: 14, fontWeight: 700, color: "var(--tprimary)" }}>{t.name}</div>
                <div className="font-body" style={{ fontSize: 12.5, color: "var(--tmuted)" }}>{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section id="faq" style={{ maxWidth: 760, margin: "0 auto", padding: "40px 22px 64px" }}>
        <div className="mkt-fade-up" style={{ textAlign: "center", marginBottom: 32 }}>
          <Eyebrow className="!justify-center" testId="faq-eyebrow">Questions</Eyebrow>
          <h2 className="font-display" style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 800, letterSpacing: "-0.025em", color: "var(--tprimary)", marginTop: 14 }}>
            Everything you're wondering
          </h2>
        </div>
        <div className="mkt-fade-up">
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} style={{ borderBottom: "1px solid var(--line)" }}>
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-display" style={{ fontSize: 16, fontWeight: 600, color: "var(--tprimary)", textAlign: "left" }}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="font-body" style={{ fontSize: 14.5, color: "var(--tsecondary)", lineHeight: 1.65 }}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section style={{ maxWidth: 920, margin: "0 auto", padding: "30px 22px 84px" }}>
        <div className="mkt-fade-up panel-card grid-pulse" style={{ padding: "52px 32px", textAlign: "center", borderColor: "var(--cardhover)" }}>
          <h2 className="font-display" style={{ fontSize: "clamp(30px, 5vw, 46px)", fontWeight: 800, letterSpacing: "-0.03em", color: "var(--tprimary)", lineHeight: 1.05 }}>
            Ready to see your brand in action?
          </h2>
          <p className="font-body" style={{ fontSize: 17, color: "var(--tsecondary)", marginTop: 16 }}>
            Paste a URL. Watch your brand come to life. That's the whole idea.
          </p>
          <button data-testid="final-cta-button" onClick={() => navigate("/start")} className="btn-primary" style={{ marginTop: 28, padding: "14px 30px", fontSize: 16 }}>
            Start Free <ArrowRight size={17} />
          </button>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
